import {
  computed,
  inject,
  Injectable,
  Injector,
  Signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  CONFIGURATION_SELECT_OPTIONS_BY_SETTING_KEY,
  CONFIGURATION_SETTING_KEYS,
  ConfigurationSettingKey,
  EConfigurationChannel,
  EEntityType,
  ESettingInputType,
  ETextType,
  IConfiguration,
  INormalText,
  ISalutation,
  LanguagePrefix,
  SalutationId,
} from '@web/dtos';
import { ConfigurationControlValue } from '../../../entities/configuration-entity';
import { CmsSalutationStore } from '../salutation/salutation-store.service';
import { ServiceAreaStore } from '../service-area/service-area.store';
import { TariffAreaStore } from '../tariff-area/tariff-area-store.service';

export enum EConfigurationCustomInput {
  ServiceArea = 'serviceArea',
  TariffAreas = 'tariffAreas',
  ProofOfPurchase = 'proofOfPurchase',
  Salutations = 'salutations',
  OperatorMappings = 'operatorMappings',
  Unsupported = 'unsupported',
}

@Injectable({
  providedIn: 'root',
})
export class ConfigurationCustomInputService {
  private readonly injector: Injector = inject(Injector);

  private get salutationStore(): CmsSalutationStore {
    return this.injector.get(CmsSalutationStore);
  }

  private get serviceAreaStore(): ServiceAreaStore {
    return this.injector.get(ServiceAreaStore);
  }

  private get tariffAreaStore(): TariffAreaStore {
    return this.injector.get(TariffAreaStore);
  }

  readonly isLoading: Signal<boolean> = computed(
    (): boolean => this.salutationStore.isLoading()
  );

  customInputType(setting: IConfiguration): EConfigurationCustomInput {
    if (setting.inputType !== ESettingInputType.Custom) {
      return EConfigurationCustomInput.Unsupported;
    }

    switch (setting.settingKey) {
      case CONFIGURATION_SETTING_KEYS.ServiceAreaId:
      case CONFIGURATION_SETTING_KEYS.SubscriptionServiceArea:
        return EConfigurationCustomInput.ServiceArea;
      case CONFIGURATION_SETTING_KEYS.UicTariffAreas:
      case CONFIGURATION_SETTING_KEYS.VdvKaTariffAreas:
        return EConfigurationCustomInput.TariffAreas;
      case CONFIGURATION_SETTING_KEYS.IsProofOfPurchaseName:
        return EConfigurationCustomInput.ProofOfPurchase;
      case CONFIGURATION_SETTING_KEYS.SalutationsRegistration:
        return EConfigurationCustomInput.Salutations;
      case CONFIGURATION_SETTING_KEYS.OperatorMappings:
        return EConfigurationCustomInput.OperatorMappings;
      default:
        return EConfigurationCustomInput.Unsupported;
    }
  }

  isSalutationsSetting(setting: IConfiguration): boolean {
    return (
      this.customInputType(setting) === EConfigurationCustomInput.Salutations
    );
  }

  isOperatorMappingsSetting(setting: IConfiguration): boolean {
    return (
      this.customInputType(setting) ===
      EConfigurationCustomInput.OperatorMappings
    );
  }

  searchableValues(
    setting: IConfiguration,
    currentValue: ConfigurationControlValue
  ): string[] {
    const selectOptions: string[] =
      CONFIGURATION_SELECT_OPTIONS_BY_SETTING_KEY[
        setting.settingKey as ConfigurationSettingKey
      ] ?? [];

    switch (this.customInputType(setting)) {
      case EConfigurationCustomInput.ServiceArea:
        return [
          ...selectOptions,
          ...this.serviceAreaStore
            .serviceAreas()
            .map((serviceArea): string => serviceArea.name),
        ];
      case EConfigurationCustomInput.TariffAreas:
        return [
          ...selectOptions,
          ...this.tariffAreaStore
            .tariffAreas()
            .map((tariffArea): string => tariffArea.name),
        ];
      case EConfigurationCustomInput.Salutations:
        return [...selectOptions, ...this.listValues(currentValue)];
      default:
        return selectOptions;
    }
  }

  fillSalutations(
    configuration: IConfiguration[],
    control: (
      setting: IConfiguration
    ) => FormControl<ConfigurationControlValue>
  ): void {
    const salutationSettings: IConfiguration[] = configuration.filter(
      (setting: IConfiguration): boolean => this.isSalutationsSetting(setting)
    );
    if (salutationSettings.length === 0) {
      return;
    }

    const salutations: ISalutation[] = this.salutationStore.salutations();

    salutationSettings.forEach((setting: IConfiguration): void => {
      const textType: ETextType | undefined = this.textTypes(setting)[0];
      if (!textType) {
        return;
      }

      control(setting).setValue(
        this.germanSalutationValues(salutations, textType),
        { emitEvent: false }
      );
    });
  }

  async saveSalutations(
    configuration: IConfiguration[],
    control: (
      setting: IConfiguration
    ) => FormControl<ConfigurationControlValue>
  ): Promise<void> {
    const salutationSettings: IConfiguration[] = configuration.filter(
      (setting: IConfiguration): boolean =>
        this.isSalutationsSetting(setting) && control(setting).dirty
    );
    if (salutationSettings.length === 0) {
      return;
    }

    const salutations: ISalutation[] = salutationSettings.reduce(
      (
        currentSalutations: ISalutation[],
        setting: IConfiguration
      ): ISalutation[] =>
        this.textTypes(setting).reduce(
          (
            updatedSalutations: ISalutation[],
            textType: ETextType
          ): ISalutation[] =>
            this.applyGermanSalutationValues(
              updatedSalutations,
              textType,
              this.listValues(control(setting).value)
            ),
          currentSalutations
        ),
      this.cloneSalutations(this.salutationStore.salutations())
    );

    const deletedIds: SalutationId[] = salutations
      .filter(
        (salutation: ISalutation): boolean => salutation.texts.length === 0
      )
      .flatMap((salutation: ISalutation): SalutationId[] =>
        salutation.id ? [salutation.id] : []
      );
    const remainingSalutations: ISalutation[] = salutations.filter(
      (salutation: ISalutation): boolean => salutation.texts.length > 0
    );

    for (const id of deletedIds) {
      await this.salutationStore.delete(id, false);
    }
    await this.salutationStore.save(remainingSalutations);
  }

  private germanSalutationValues(
    salutations: ISalutation[],
    textType: ETextType
  ): string[] {
    return salutations.flatMap((salutation: ISalutation): string[] => {
      const normalText: INormalText | undefined = salutation.texts.find(
        (text: INormalText): boolean => text.textType === textType
      );
      const value: string | undefined = normalText?.text.values.find(
        textValue => textValue.languagePrefix === 'DE'
      )?.value;

      return value === undefined ? [] : [value];
    });
  }

  private applyGermanSalutationValues(
    salutations: ISalutation[],
    textType: ETextType,
    values: string[]
  ): ISalutation[] {
    const result: ISalutation[] = [...salutations];
    const itemCount: number = Math.max(result.length, values.length);

    for (let index = 0; index < itemCount; index++) {
      const value: string | undefined = values[index];
      const salutation: ISalutation = result[index] ?? { texts: [] };
      const textIndex: number = salutation.texts.findIndex(
        (text: INormalText): boolean => text.textType === textType
      );

      if (value === undefined) {
        if (textIndex >= 0) {
          salutation.texts.splice(textIndex, 1);
        }
        result[index] = salutation;
        continue;
      }

      if (textIndex < 0) {
        salutation.texts.push({
          textType,
          entityType: EEntityType.salutation,
          relationType: index.toString(),
          text: {
            values: [
              {
                languagePrefix: 'DE' as LanguagePrefix,
                value,
              },
            ],
          },
        });
      } else {
        const normalText: INormalText = salutation.texts[textIndex];
        normalText.relationType = index.toString();
        const germanValue = normalText.text.values.find(
          textValue => textValue.languagePrefix === 'DE'
        );
        if (germanValue) {
          germanValue.value = value;
        } else {
          normalText.text.values.push({
            languagePrefix: 'DE' as LanguagePrefix,
            value,
          });
        }
      }

      result[index] = salutation;
    }

    return result;
  }

  private cloneSalutations(salutations: ISalutation[]): ISalutation[] {
    return salutations.map(
      (salutation: ISalutation): ISalutation => ({
        ...salutation,
        texts: salutation.texts.map(
          (normalText: INormalText): INormalText => ({
            ...normalText,
            text: {
              ...normalText.text,
              values: normalText.text.values.map(textValue => ({
                ...textValue,
              })),
            },
          })
        ),
      })
    );
  }

  private listValues(value: ConfigurationControlValue): string[] {
    return Array.isArray(value)
      ? value.map(item => item.toString().trim()).filter(Boolean)
      : [];
  }

  private textTypes(setting: IConfiguration): ETextType[] {
    return setting.channels.map(
      (channel: EConfigurationChannel): ETextType =>
        channel === EConfigurationChannel.App ? ETextType.App : ETextType.Web
    );
  }
}
